import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getMyEmails } from '../api/users'
import type { UserEmailOut } from '../api/users'
import { apiFetch } from '../api/client'
import { documentLabelsForLocale } from '../config/locales'
import type { CountryCode } from '../config/locales'
import type { DocType } from '../types/testdata.types'
import { documentDisplayLabel } from '../hooks/testdata-form/documentDisplayLabel'
import { FINANCIAL_SCENARIO_OPTIONS } from '../hooks/testdata-form/constants'
import { XML_FORMAT_LABELS, FACTURX_PROFILE_LABELS } from '../hooks/testdata-form/formatLabels'
import {
  buildSendConfirmationSummary, buildSendEmailPayload, createSendGate, embedOptionApplies, isFacturxSnapshot,
} from '../hooks/testdata-form/sendConfirmation'
import type { EmailRequestSnapshot, SendConfirmationContext } from '../hooks/testdata-form/sendConfirmation'

export interface TestdataEmailPayload {
  to_address: string
  [key: string]: unknown
}

interface Props {
  /** REQ-TESTDATA-011: the frozen request snapshot taken by `openEmailModal`
   * after validation. The summary AND the POST are derived from this object
   * only — never from live generator state. */
  requestBody: EmailRequestSnapshot
  /** Pre-submit resync (REQ-TESTDATA-001): auto-managed -> verse server-
   * suggestie; user-managed -> ongewijzigd, geen fetch. De payload MOET de
   * hieruit geretourneerde waarde gebruiken. */
  resolveDocumentNumber: (snapshotNumber: string) => Promise<string>
  onClose: () => void
  onSent?: () => void
}

const DOC_TYPE_KEYS: Record<string, string> = {
  invoice: 'testdata_email_modal.doc_invoice',
  order_response: 'testdata_email_modal.doc_order_response',
  despatch_advice: 'testdata_email_modal.doc_despatch_advice',
  credit_note: 'testdata_email_modal.doc_credit_note',
}

const SCENARIO_LABEL_KEY = Object.fromEntries(
  FINANCIAL_SCENARIO_OPTIONS.map(o => [o.value, o.labelKey])
) as Record<string, string>

const PREFS_KEY = 'testdata_email_prefs'

const FOCUSABLE = 'button:not([disabled]), select:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900'

function loadPrefs(): { email: string; includePdf: boolean; includeXml: boolean; embedPdfInXml: boolean } | null {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function savePrefs(email: string, includePdf: boolean, includeXml: boolean, embedPdfInXml: boolean) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify({ email, includePdf, includeXml, embedPdfInXml })) } catch { /* quota */ }
}

function languageName(code: string, uiLanguage: string): string {
  try {
    return new Intl.DisplayNames([uiLanguage], { type: 'language' }).of(code) ?? code
  } catch {
    return code
  }
}

function SummaryGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h3>
      <dl className="grid grid-cols-[8rem_1fr] sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-1 text-sm">{children}</dl>
    </section>
  )
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="min-w-0 font-medium text-gray-900 dark:text-gray-100 [overflow-wrap:anywhere]">{children}</dd>
    </>
  )
}

export function TestdataEmailModal({ requestBody, resolveDocumentNumber, onClose, onSent }: Props) {
  const { t, i18n } = useTranslation()
  const titleId = useId()
  const introId = useId()
  const recipientId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const sendGate = useRef(createSendGate())
  const isFacturx = isFacturxSnapshot(requestBody)
  const prefs = loadPrefs()
  const [emailAddresses, setEmailAddresses] = useState<UserEmailOut[]>([])
  const [selectedEmail, setSelectedEmail] = useState(prefs?.email ?? '')
  const [includePdf, setIncludePdf] = useState(isFacturx ? true : (prefs?.includePdf ?? true))
  const [includeXml, setIncludeXml] = useState(isFacturx ? false : (prefs?.includeXml ?? true))
  const [embedPdfInXml, setEmbedPdfInXml] = useState(prefs?.embedPdfInXml ?? true)
  // CAP-013 / REQ-TESTREF-010: Ground Truth is a single central choice made in
  // the main generator (requestBody.include_ground_truth) — this modal only
  // displays it read-only and must never re-decide it independently.
  const includeGroundTruth = Boolean(requestBody.include_ground_truth)
  const [subjectNote, setSubjectNote] = useState('')
  const [phase, setPhase] = useState<'confirm' | 'sending' | 'done' | 'error'>('confirm')
  const phaseRef = useRef(phase)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState('')
  const [sentDocNumber, setSentDocNumber] = useState('')
  const [context, setContext] = useState<SendConfirmationContext | null>(null)
  const [contextState, setContextState] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => { phaseRef.current = phase }, [phase])

  useEffect(() => {
    getMyEmails().then(list => {
      setEmailAddresses(list)
      const saved = prefs?.email
      const match = saved ? list.find(e => e.email === saved) : null
      if (match) {
        setSelectedEmail(match.email)
      } else if (!selectedEmail) {
        const primary = list.find(e => e.is_primary) ?? list[0]
        if (primary) setSelectedEmail(primary.email)
      }
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // REQ-TESTDATA-011: sender + date/number notation come from the server
  // (mail config + canonical locale profiles), for the SNAPSHOT's locale/date.
  // Display-only: a failure never blocks sending, the payload does not use it.
  useEffect(() => {
    const params = new URLSearchParams({
      locale: String(requestBody.locale ?? ''),
      document_date: String(requestBody.document_date ?? ''),
    })
    apiFetch<SendConfirmationContext>(`/api/testdata/send-confirmation-context?${params}`)
      .then(ctx => { setContext(ctx); setContextState('ready') })
      .catch(() => setContextState('error'))
  }, [requestBody])

  // Dialog semantics: focus the dialog itself (not the primary action, so an
  // Enter from the trigger can never send), Escape cancels unless a send is in
  // flight, and focus returns to the trigger on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phaseRef.current !== 'sending' && !sendGate.current.inFlight) {
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previouslyFocused?.focus?.()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trapFocus = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialogRef.current) return
    const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    const active = document.activeElement
    if (e.shiftKey && (active === first || active === dialogRef.current)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const summary = buildSendConfirmationSummary(requestBody, { toAddress: selectedEmail }, context)
  const canSend = selectedEmail.trim() !== '' && (isFacturx || includePdf || includeXml)
  const showEmbedOption = embedOptionApplies(requestBody, includeXml)

  async function handleSend() {
    if (!canSend || phase !== 'confirm') return
    if (!sendGate.current.tryAcquire()) return
    setPhase('sending')
    setErrorMsg(null)
    try {
      const freshNumber = await resolveDocumentNumber(String(requestBody.document_number ?? ''))
      const choices = { toAddress: selectedEmail, includePdf, includeXml, embedPdfInXml, subjectNote }
      await apiFetch('/api/testdata/send-email', {
        method: 'POST',
        body: JSON.stringify(buildSendEmailPayload(requestBody, choices, freshNumber)),
      })
      savePrefs(selectedEmail, includePdf, includeXml, embedPdfInXml)
      setSentTo(selectedEmail)
      setSentDocNumber(freshNumber)
      setPhase('done')
      onSent?.()
    } catch (e: unknown) {
      setErrorMsg(e instanceof Error ? e.message : t('testdata_email_modal.error_send'))
      setPhase('error')
    } finally {
      sendGate.current.release()
    }
  }

  const docType = summary.documentType as DocType
  const docLabelKey = DOC_TYPE_KEYS[docType]
  const uiDocLabel = docLabelKey ? t(docLabelKey) : docType
  const docLabel = summary.country
    ? documentDisplayLabel(uiDocLabel, documentLabelsForLocale(summary.country as CountryCode, summary.locale)?.[docType])
    : uiDocLabel
  const profileLabel = summary.facturxProfile
    ? FACTURX_PROFILE_LABELS[summary.facturxProfile] ?? summary.facturxProfile
    : summary.xmlProfile
    ? t(`test_data_generator.xml_profile_${summary.xmlProfile}`)
    : null
  const formatLabel = [docLabel, XML_FORMAT_LABELS[summary.xmlFormat] ?? summary.xmlFormat, profileLabel]
    .filter(Boolean).join(' · ')
  const pending = contextState === 'loading'
    ? t('testdata_email_modal.value_loading')
    : t('testdata_email_modal.value_unavailable')
  const scenarioKey = summary.financialScenarioId ? SCENARIO_LABEL_KEY[summary.financialScenarioId] : undefined
  const isInvoiceLike = docType === 'invoice' || docType === 'credit_note'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={introId}
        tabIndex={-1}
        onKeyDown={trapFocus}
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-5 focus:outline-none"
      >

        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {t('testdata_email_modal.confirm_title')}
            </h2>
            <p id={introId} className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {t('testdata_email_modal.confirm_intro')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={phase === 'sending'}
            aria-label={t('common.close')}
            title={t('common.close')}
            className={`shrink-0 rounded px-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40 transition-colors text-lg leading-none ${focusRing}`}
          >
            ×
          </button>
        </div>

        {phase === 'confirm' && (
          <>
            <div className="space-y-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
              <SummaryGroup title={t('testdata_email_modal.group_email')}>
                <SummaryRow label={t('testdata_email_modal.row_sender')}>
                  {summary.sender ?? (contextState === 'ready' ? t('testdata_email_modal.sender_not_configured') : pending)}
                </SummaryRow>
                <SummaryRow label={t('testdata_email_modal.row_recipient')}>
                  {selectedEmail || (
                    <span className="font-normal text-gray-500 dark:text-gray-400">{t('testdata_email_modal.no_emails_configured')}</span>
                  )}
                  {emailAddresses.length > 1 && (
                    <>
                      <label htmlFor={recipientId} className="sr-only">{t('testdata_email_modal.change_recipient')}</label>
                      <select
                        id={recipientId}
                        value={selectedEmail}
                        onChange={e => setSelectedEmail(e.target.value)}
                        title={t('testdata_email_modal.change_recipient')}
                        className="mt-1 block w-full max-w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-normal text-gray-700 dark:text-gray-200 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {emailAddresses.map(addr => (
                          <option key={addr.id} value={addr.email}>
                            {addr.email}{addr.label ? ` (${addr.label})` : ''}{addr.is_primary ? ' ★' : ''}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </SummaryRow>
              </SummaryGroup>

              <SummaryGroup title={t('testdata_email_modal.group_document')}>
                <SummaryRow label={t('testdata_email_modal.row_format')}>{formatLabel}</SummaryRow>
                <SummaryRow label={t('testdata_email_modal.row_locale')}>{summary.countryName} · {summary.locale}</SummaryRow>
                <SummaryRow label={t('testdata_email_modal.row_language')}>
                  {summary.language ? languageName(summary.language, i18n.resolvedLanguage ?? i18n.language) : pending}
                </SummaryRow>
                <SummaryRow label={t('testdata_email_modal.row_currency')}>{summary.currency}</SummaryRow>
              </SummaryGroup>

              <SummaryGroup title={t('testdata_email_modal.group_testdata')}>
                {summary.financialScenarioId && (
                  <SummaryRow label={t('testdata_email_modal.row_scenario')}>
                    {summary.financialScenarioId}{scenarioKey ? ` · ${t(scenarioKey)}` : ''}
                  </SummaryRow>
                )}
                <SummaryRow label={t(isInvoiceLike ? 'testdata_email_modal.row_line_count_invoice' : 'testdata_email_modal.row_line_count')}>
                  {summary.lineCount}
                </SummaryRow>
                <SummaryRow label={t('testdata_email_modal.row_date_format')}>{summary.dateExample ?? pending}</SummaryRow>
                <SummaryRow label={t('testdata_email_modal.row_number_format')}>{summary.amountExample ?? pending}</SummaryRow>
              </SummaryGroup>
            </div>

            {isFacturx ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('testdata_email_modal.facturx_attachment_note')}
              </p>
            ) : (
              <fieldset className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <legend className="sr-only">{t('testdata_email_modal.attachments_legend')}</legend>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={includePdf}
                    onChange={e => setIncludePdf(e.target.checked)}
                    className="w-4 h-4 rounded accent-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">PDF</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={includeXml}
                    onChange={e => setIncludeXml(e.target.checked)}
                    className="w-4 h-4 rounded accent-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">XML</span>
                </label>
                {!includePdf && !includeXml && (
                  <span role="alert" className="text-xs text-red-600 dark:text-red-400">⚠ {t('testdata_email_modal.select_attachment')}</span>
                )}
              </fieldset>
            )}

            {showEmbedOption && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={embedPdfInXml}
                  onChange={e => setEmbedPdfInXml(e.target.checked)}
                  className="w-4 h-4 rounded accent-blue-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('testdata_email_modal.embed_pdf_label')}</span>
              </label>
            )}

            {/* CAP-013 / REQ-TESTREF-010: read-only — Ground Truth is decided once,
                centrally, in the main generator. This modal only reports it. */}
            <div className="flex items-start gap-2.5 rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50">
              <svg aria-hidden="true" className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {includeGroundTruth
                  ? t('testdata_email_modal.ground_truth_included_note')
                  : t('testdata_email_modal.ground_truth_excluded_note')}
              </span>
            </div>

            <div className="space-y-2">
              <label htmlFor={`${recipientId}-subject`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('testdata_email_modal.subject_note_label')}
              </label>
              <input
                id={`${recipientId}-subject`}
                type="text"
                value={subjectNote}
                onChange={e => setSubjectNote(e.target.value)}
                maxLength={150}
                placeholder={t('testdata_email_modal.subject_note_placeholder')}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-wrap justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors ${focusRing}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend || phase !== 'confirm'}
                className={`flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800 text-white text-sm font-semibold rounded-lg transition-colors ${focusRing}`}
              >
                <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('testdata_email_modal.btn_confirm_send')}
              </button>
            </div>
          </>
        )}

        {phase === 'sending' && (
          <div role="status" className="flex items-center gap-3 py-4 text-sm text-gray-600 dark:text-gray-400">
            <svg aria-hidden="true" className="w-5 h-5 text-blue-500 animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            {t('testdata_email_modal.sending_to', { email: selectedEmail })}
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-4">
            <div role="status" className="flex items-start gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl p-4">
              <span aria-hidden="true" className="shrink-0 w-6 h-6 rounded-full bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300 flex items-center justify-center">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-green-800 dark:text-green-200 [overflow-wrap:anywhere]">
                  {t('testdata_email_modal.done_title')}: {docLabel} {sentDocNumber}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-0.5 [overflow-wrap:anywhere]">{sentTo}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className={`px-5 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors ${focusRing}`}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="space-y-4">
            <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4 text-sm text-red-700 dark:text-red-300 [overflow-wrap:anywhere]">
              ⚠ {errorMsg ?? t('testdata_email_modal.error_unknown')}
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors ${focusRing}`}
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                onClick={() => setPhase('confirm')}
                className={`px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors ${focusRing}`}
              >
                {t('testdata_email_modal.btn_retry')}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
